/**
 * Guest access is time-boxed, and the client keeps a courtesy timer so a guest
 * whose window closes mid-session is shown the door instead of quietly losing
 * every action to a 403. The server stays authoritative: it refuses the next
 * join and every REST call regardless of what the browser thinks.
 *
 * Spec: docs/PLATFORM_ROLES_AND_ORGANIZATIONS.md §6.2.
 */

/**
 * `setTimeout` stores its delay in a signed 32-bit integer, so anything past
 * ~24.8 days overflows and fires immediately. Long guest windows are therefore
 * split: the timer wakes at 24 days and schedules the remainder.
 */
export const MAX_GUEST_TIMER_MS = 24 * 24 * 60 * 60 * 1000;

/** What the next timer should do when it fires. */
export interface GuestTimerPlan {
  /** Milliseconds to wait, never negative and never past the clamp. */
  delayMs: number;
  /** True when the wait ends at the expiry itself rather than at the clamp. */
  isFinal: boolean;
}

/**
 * How long to wait before acting on a guest expiry, and whether that wait ends
 * the guest's access or is only one leg of a long one.
 *
 * @param expiresAt - the permission's expiry, as an ISO string or a Date
 * @param now - the current instant in milliseconds, injected for testability
 * @returns the next timer, or null when there is nothing to schedule
 */
export function computeGuestTimerDelay(
  expiresAt: string | Date | null | undefined,
  now: number = Date.now()
): GuestTimerPlan | null {
  if (!expiresAt) {
    return null;
  }

  const expiry = expiresAt instanceof Date ? expiresAt.getTime() : Date.parse(expiresAt);
  if (Number.isNaN(expiry)) {
    return null;
  }

  const remaining = expiry - now;

  // Already lapsed: act now rather than schedule a negative delay.
  if (remaining <= 0) {
    return { delayMs: 0, isFinal: true };
  }

  return remaining > MAX_GUEST_TIMER_MS
    ? { delayMs: MAX_GUEST_TIMER_MS, isFinal: false }
    : { delayMs: remaining, isFinal: true };
}

/** True when a guest permission's expiry has already passed. */
export function isGuestAccessExpired(
  expiresAt: string | Date | null | undefined,
  now: number = Date.now()
): boolean {
  if (!expiresAt) {
    return false;
  }

  const expiry = expiresAt instanceof Date ? expiresAt.getTime() : Date.parse(expiresAt);
  return !Number.isNaN(expiry) && expiry <= now;
}

/** The date a guest's access runs out, for "Guest until <date>" labels. */
export function formatGuestExpiry(expiresAt: string | Date): string {
  const expiry = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);
  return Number.isNaN(expiry.getTime()) ? '' : expiry.toLocaleDateString();
}
